import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Grid, IconButton, Box, Alert, Typography, Select, FormControl, InputLabel, Divider, OutlinedInput, Tooltip,
  CircularProgress,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useApi } from '../../../hooks/useApi';
import { useStateValue } from '../../../context/StateValueContext';
import { usePpAccounts } from '../../../hooks/usePpAccounts';
import CompanySelect from '../../../components/common/CompanySelect';
import PpLogo from '../../../components/common/PpLogo';
import GatewayMultiSelect from './GatewayMultiSelect';
import { formatPpAccountLabel } from '../../../utils/ppAccountLabel';
import { PP_TYPES, CRM_TYPES, CRM_LABELS } from '../../../types/alertEnrolment';
import type {
  AdyenMerchantOption, CheckoutChampGatewayOption, CrmConnectorGatewayOption, CrmType, Enrolment, EnrolmentCreateBody,
  EnrolmentMappingInput, EnrolmentWriteBody, PpType,
} from '../../../types/alertEnrolment';
import type { Brand } from '../../../types/paymentProcessor';
import { SINGLE_BRAND_PLATFORMS } from './enrolmentConstants';
import { BRAND_LABEL } from '../../../constants/brand';

interface ComboRow { id?: number; bin: string; caid: string; mcc: string; }
const emptyCombo = (): ComboRow => ({ bin: '', caid: '', mcc: '' });
const PP_OTHER = '__other__';
const CRM_OTHER = '__crm_other__';
// The old form let an enrolment name a CRM platform without naming an account, so these strings
// survive on legacy rows and mean the opposite of what they look like: no account is linked.
const isCrmPlatformLabel = (v: string) =>
  Object.values(CRM_LABELS).some((label) => label.toLowerCase() === v.trim().toLowerCase());
// PP account ids are unique only within a pp_type, so the option key composes both (Disputes convention).
const ppAccountKey = (a: { ppType: string; ppAccountId: number }) => `${a.ppType}:${a.ppAccountId}`;

export default function EnrolmentFormDialog({ open, editing, defaultCompanyId, apiBase = '/api/alert-enrolments', hideCompanySelect = false, onClose, onSaved }: {
  open: boolean; editing: Enrolment | null; defaultCompanyId: number | null;
  apiBase?: string; hideCompanySelect?: boolean; onClose: () => void; onSaved: () => void;
}) {
  const { post, put, loading } = useApi();
  // Its own instance: saving must not drive the merchant lookup's spinner, and vice versa.
  const { get: getMerchants, loading: merchantsLoading } = useApi();
  // And its own again: the two sub-account lookups run against different accounts, so one must not
  // blank the other's spinner.
  const { get: getGateways, loading: gatewaysLoading } = useApi();
  const { get: getCrmGateways, loading: crmGatewaysLoading } = useApi();
  const { get: getCompanyBrands } = useApi();
  const [{ miniCompanies }] = useStateValue();
  const { ppAccounts } = usePpAccounts();

  const [companyId, setCompanyId] = useState<number | ''>('');
  const [ppSelection, setPpSelection] = useState<string>('');
  const [ppOtherName, setPpOtherName] = useState('');
  const [crmSelection, setCrmSelection] = useState<string>('');
  const [crmText, setCrmText] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState<number | ''>('');
  const [companyBrands, setCompanyBrands] = useState<Brand[]>([]);
  const [descriptors, setDescriptors] = useState('');
  const [historic, setHistoric] = useState('');
  // Remembers the loaded descriptors' id per exact text, so an unchanged/moved descriptor keeps its id (and verification).
  const descriptorIdByText = useRef<Map<string, number>>(new Map());
  const [combos, setCombos] = useState<ComboRow[]>([emptyCombo()]);
  const [merchantCode, setMerchantCode] = useState('');
  const binRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusRow, setFocusRow] = useState<number | null>(null);
  const [merchants, setMerchants] = useState<AdyenMerchantOption[] | null>(null);
  const [merchantError, setMerchantError] = useState<string | null>(null);
  const [billerIds, setBillerIds] = useState<number[]>([]);
  const [gateways, setGateways] = useState<CheckoutChampGatewayOption[] | null>(null);
  const [gatewayError, setGatewayError] = useState<string | null>(null);
  const [gatewayIds, setGatewayIds] = useState<string[]>([]);
  const [crmGateways, setCrmGateways] = useState<CrmConnectorGatewayOption[] | null>(null);
  const [crmGatewayError, setCrmGatewayError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const companyAccounts = useMemo(
    () =>
      ppAccounts
        // In client mode /api/pp-accounts already returns only this company's accounts.
        .filter((a) => hideCompanySelect || (companyId !== '' && a.companyId === Number(companyId)))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [ppAccounts, companyId, hideCompanySelect],
  );
  // The two lists overlap on Shopify - Shopify Payments is a processor as well as a CRM - so an
  // account is offered in whichever selects its own type is valid for, not in exactly one.
  const isPpType = (t: string) => (PP_TYPES as readonly string[]).includes(t);
  const isCrmType = (t: string) => (CRM_TYPES as readonly string[]).includes(t);
  // Other accounts are not wired to enrolments yet: the processor slot still takes them as free text.
  const processorAccounts = companyAccounts.filter((a) => isPpType(a.ppType) && a.ppType !== 'other');
  const crmAccounts = companyAccounts.filter((a) => isCrmType(a.ppType));
  const selectedCrmAccount = crmAccounts.find((a) => ppAccountKey(a) === crmSelection) ?? null;
  const selectedPpAccount = processorAccounts.find((a) => ppAccountKey(a) === ppSelection) ?? null;
  // One stored Adyen account is an Adyen company spanning many merchant accounts, so the account
  // alone cannot say which merchant a BIN/CAID pair settles through - the code has to be mapped.
  const showMerchants = selectedPpAccount?.ppType === 'adyen';
  // A saved code Adyen has since dropped must stay selectable - otherwise the Select silently
  // blanks a mapping the admin never cleared.
  const isUnlisted = showMerchants && merchants != null && merchantCode !== ''
    && !merchants.some((m) => m.id === merchantCode);
  // A merchant another enrolment settles through: offered but not selectable, since the save would
  // be rejected. The enrolment being edited is not blocked from keeping its own merchant.
  const isClaimedByOther = (mappedEnrolmentId: number | null) =>
    mappedEnrolmentId != null && mappedEnrolmentId !== editing?.id;
  // A CoC gateway is a single MID, and an enrolment holding several BIN/CAID pairs settles through
  // several of them - so unlike the Adyen merchant this is a multi-select.
  const showGateways = selectedCrmAccount?.ppType === 'checkout_champ';
  // The fetched list second, so a gateway CoC still lists is titled by what it is called now
  // rather than by whatever it was called when the enrolment was saved.
  const gatewayTitles = new Map<number, string | null>([
    ...(editing?.checkoutChampGateways ?? []).map((g) => [g.billerId, g.title] as const),
    ...(gateways ?? []).map((g) => [g.billerId, g.title] as const),
  ]);
  const gatewayLabel = (billerId: number) => gatewayTitles.get(billerId) ?? String(billerId);
  // Billers this enrolment already settles through that CoC no longer offers. They stay selected
  // rather than being silently cleared out of a mapping nobody touched - same rule as the Adyen
  // merchant. Empty until the list arrives, so nothing is called delisted before there is a list
  // to be absent from.
  const selectedDelistedBillerIds = gateways == null
    ? []
    : billerIds.filter((id) => !gateways.some((g) => g.billerId === id));
  const showCrmGateways = selectedCrmAccount?.ppType === 'crm_connector';
  const crmGatewayTitles = new Map<string, string | null>([
    ...(editing?.crmConnectorGateways ?? []).map((g) => [g.id, g.name] as const),
    ...(crmGateways ?? []).map((g) => [g.id, g.name] as const),
  ]);
  const selectedDelistedGatewayIds = crmGateways == null
    ? []
    : gatewayIds.filter((id) => !crmGateways.some((g) => g.id === id));
  const isSingleBrandPlatform = (t: string) => (SINGLE_BRAND_PLATFORMS as readonly string[]).includes(t);
  const derivedBrandAccount = selectedPpAccount && isSingleBrandPlatform(selectedPpAccount.ppType)
    ? selectedPpAccount
    : selectedCrmAccount && isSingleBrandPlatform(selectedCrmAccount.ppType)
      ? selectedCrmAccount
      : null;

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editing) {
      setCompanyId(editing.companyId);
      setSelectedBrandId(editing.brandId ?? '');
      // A CRM that is not one of the company's accounts keeps its name as free text under 'Other',
      // unless it names nothing at all: blank, or a bare platform label left by the old form.
      if (editing.crmType != null && editing.crmAccountId != null) {
        setCrmSelection(`${editing.crmType}:${editing.crmAccountId}`); setCrmText('');
      } else if (!editing.crm.trim() || isCrmPlatformLabel(editing.crm)) {
        setCrmSelection(''); setCrmText('');
      } else {
        setCrmSelection(CRM_OTHER); setCrmText(editing.crm);
      }
      const idMap = new Map<string, number>();
      for (const d of [...editing.descriptors, ...editing.historicDescriptors]) idMap.set(d.descriptor, d.id);
      descriptorIdByText.current = idMap;
      setDescriptors(editing.descriptors.map((d) => d.descriptor).join(', '));
      setHistoric(editing.historicDescriptors.map((d) => d.descriptor).join(', '));
      setMerchantCode(editing.adyenMerchantAccountCode ?? '');
      setBillerIds(editing.checkoutChampGateways.map((g) => g.billerId));
      setGatewayIds(editing.crmConnectorGateways.map((g) => g.id));
      setCombos(editing.combinations.length
        ? editing.combinations.map((c) => ({ id: c.id, bin: c.bin, caid: c.caid, mcc: c.mcc ?? '' }))
        : [emptyCombo()]);
    } else {
      setCompanyId(defaultCompanyId ?? '');
      descriptorIdByText.current = new Map();
      setPpSelection(''); setPpOtherName(''); setCrmSelection(''); setCrmText(''); setSelectedBrandId('');
      setDescriptors(''); setHistoric(''); setCombos([emptyCombo()]); setMerchantCode(''); setBillerIds([]); setGatewayIds([]);
    }
  }, [open, editing, defaultCompanyId]);

  // Resolve the PP-account dropdown separately: a late /api/pp-accounts response must
  // re-match the edited account WITHOUT re-running the reset above, which would wipe input.
  useEffect(() => {
    if (!open || !editing) return;
    if (editing.ppAccountId != null) {
      const match = ppAccounts.find((a) => a.ppType === editing.ppType && a.ppAccountId === editing.ppAccountId);
      if (match) { setPpSelection(ppAccountKey(match)); setPpOtherName(''); return; }
    }
    setPpSelection(PP_OTHER); setPpOtherName(editing.ppAccountName ?? '');
  }, [open, editing, ppAccounts]);

  useEffect(() => {
    let current = true;
    if (!open || !showMerchants || selectedPpAccount == null) { setMerchants(null); setMerchantError(null); return; }
    setMerchantError(null);
    const companyQuery = hideCompanySelect || companyId === '' ? '' : `?companyId=${companyId}`;
    getMerchants<{ merchants: AdyenMerchantOption[] }>(`${apiBase}/adyen-merchants/${selectedPpAccount.ppAccountId}${companyQuery}`)
      .then((res) => { if (current) setMerchants(res.merchants); })
      // An empty dropdown would read as "this account has no merchants", which is a different fact.
      .catch((e: any) => { if (current) { setMerchants(null); setMerchantError(e.message ?? 'Failed to load Adyen merchants'); } });
    return () => { current = false; };
  }, [open, showMerchants, selectedPpAccount, getMerchants, apiBase, companyId, hideCompanySelect]);

  useEffect(() => {
    let current = true;
    if (!open || !showGateways || selectedCrmAccount == null) { setGateways(null); setGatewayError(null); return; }
    setGatewayError(null);
    const companyQuery = hideCompanySelect || companyId === '' ? '' : `?companyId=${companyId}`;
    getGateways<{ gateways: CheckoutChampGatewayOption[] }>(`${apiBase}/checkout-champ-gateways/${selectedCrmAccount.ppAccountId}${companyQuery}`)
      .then((res) => { if (current) setGateways(res.gateways); })
      // An empty list would read as "this account has no gateways", which is a different fact.
      .catch((e: any) => { if (current) { setGateways(null); setGatewayError(e.message ?? 'Failed to load CheckoutChamp gateways'); } });
    return () => { current = false; };
  }, [open, showGateways, selectedCrmAccount, getGateways, apiBase, companyId, hideCompanySelect]);

  useEffect(() => {
    let current = true;
    if (!open || !showCrmGateways || selectedCrmAccount == null) { setCrmGateways(null); setCrmGatewayError(null); return; }
    setCrmGatewayError(null);
    const companyQuery = hideCompanySelect || companyId === '' ? '' : `?companyId=${companyId}`;
    getCrmGateways<{ gateways: CrmConnectorGatewayOption[] }>(`${apiBase}/crm-connector-gateways/${selectedCrmAccount.ppAccountId}${companyQuery}`)
      .then((res) => { if (current) setCrmGateways(res.gateways); })
      .catch((e: any) => { if (current) { setCrmGateways(null); setCrmGatewayError(e.message ?? 'Failed to load CRM connector gateways'); } });
    return () => { current = false; };
  }, [open, showCrmGateways, selectedCrmAccount, getCrmGateways, apiBase, companyId, hideCompanySelect]);

  const fetchCompanyBrands = useCallback(() => {
    if (hideCompanySelect || companyId === '') { setCompanyBrands([]); return; }
    getCompanyBrands<Brand[]>(`/api/companies/${companyId}/brands`)
      .then((brands) => {
        setCompanyBrands(brands);
        if (!editing && brands.length === 1) setSelectedBrandId((prev) => (prev === '' ? brands[0].brandId : prev));
      })
      .catch((e: any) => setError(e.message ?? 'Failed to load brands'));
  }, [getCompanyBrands, hideCompanySelect, companyId, editing]);

  useEffect(() => {
    if (!open) return;
    fetchCompanyBrands();
  }, [open, fetchCompanyBrands]);

  const resolvePpAccount = (): { ppType: PpType; ppAccountId: number | null; ppAccountName: string | null } | null => {
    if (ppSelection === PP_OTHER) {
      const name = ppOtherName.trim();
      return name ? { ppType: 'other', ppAccountId: null, ppAccountName: name } : null;
    }
    if (ppSelection) {
      const acc = ppAccounts.find((a) => ppAccountKey(a) === ppSelection);
      if (acc) return { ppType: acc.ppType as PpType, ppAccountId: acc.ppAccountId, ppAccountName: null };
    }
    return null;
  };

  // The CRM is optional: naming neither an account nor a name is the "not stated yet" state, and
  // stores as an empty label rather than blocking the save.
  const resolveCrm = (): { crm: string; crmType: CrmType | null; crmAccountId: number | null } => {
    const unset = { crm: '', crmType: null, crmAccountId: null };
    if (crmSelection === CRM_OTHER) {
      const name = crmText.trim();
      return name ? { crm: name, crmType: null, crmAccountId: null } : unset;
    }
    if (!selectedCrmAccount) return unset;
    return { crm: '', crmType: selectedCrmAccount.ppType as CrmType, crmAccountId: selectedCrmAccount.ppAccountId };
  };

  // One list whatever the provider, and only for the slot that names an account of that provider -
  // a merchant code under a non-Adyen processor, or gateways under a non-CoC CRM, identify nothing.
  const buildMappings = (): EnrolmentMappingInput[] => {
    const mappings: EnrolmentMappingInput[] = [];
    if (showMerchants && merchantCode) {
      const listed = merchants?.find((m) => m.id === merchantCode);
      mappings.push({ provider: 'adyen', externalRef: merchantCode, externalName: listed?.name ?? null });
    }
    if (showGateways) {
      // gatewayTitles, not the fetched list alone: a delisted biller has no entry there, and
      // reading gateways directly would overwrite its stored title with null on this save.
      for (const billerId of billerIds) {
        mappings.push({ provider: 'checkout_champ', externalRef: String(billerId), externalName: gatewayTitles.get(billerId) ?? null });
      }
    }
    if (showCrmGateways) {
      for (const id of gatewayIds) {
        mappings.push({ provider: 'crm_connector', externalRef: id, externalName: crmGatewayTitles.get(id) ?? null });
      }
    }
    return mappings;
  };

  const resolveBrandId = (): number | null => {
    if (hideCompanySelect || derivedBrandAccount) return null;
    return selectedBrandId === '' ? null : selectedBrandId;
  };

  const toDescriptorInputs = (text: string) =>
    text.split(',').map((s) => s.trim()).filter(Boolean).map((descriptor) => ({ id: descriptorIdByText.current.get(descriptor), descriptor }));

  // Copies CAID + MCC to a fresh row and leaves BIN empty: the reported case is several BINs
  // sharing one CAID/MCC. No id, so reconcileCombinations inserts instead of updating the source.
  const copyComboRow = (i: number) => {
    setCombos((p) => [...p.slice(0, i + 1), { bin: '', caid: p[i].caid, mcc: p[i].mcc }, ...p.slice(i + 1)]);
    setFocusRow(i + 1);
  };

  useEffect(() => {
    if (focusRow == null) return;
    binRefs.current[focusRow]?.focus();
    setFocusRow(null);
  }, [focusRow]);

  const save = async () => {
    setError(null);
    const pp = resolvePpAccount();
    if (!pp) { setError('PP account is required'); return; }
    const writeBody: EnrolmentWriteBody = {
      ppType: pp.ppType,
      ppAccountId: pp.ppAccountId,
      ppAccountName: pp.ppAccountName,
      ...resolveCrm(),
      mappings: buildMappings(),
      brandId: resolveBrandId(),
      descriptors: toDescriptorInputs(descriptors),
      historicDescriptors: toDescriptorInputs(historic),
      combinations: combos
        .filter((c) => c.bin.trim() || c.caid.trim() || c.mcc.trim())
        .map((c) => ({ id: c.id, bin: c.bin.trim(), caid: c.caid.trim(), mcc: c.mcc.trim() || null })),
      note: editing?.note ?? null,
    };
    try {
      if (editing) {
        await put(`${apiBase}/${editing.id}`, writeBody, { slowApi: true });
      } else if (hideCompanySelect) {
        await post(apiBase, writeBody, { slowApi: true });
      } else {
        if (!companyId) { setError('Select a company'); return; }
        const createBody: EnrolmentCreateBody = { ...writeBody, companyId: Number(companyId) };
        await post(apiBase, createBody, { slowApi: true });
      }
      onSaved(); onClose();
    } catch (e: any) { setError(e.message ?? 'Failed to save enrolment'); }
  };

  const companyDisabled = Boolean(editing) || defaultCompanyId != null;

  const renderPpValue = (val: string) => {
    if (val === PP_OTHER) return 'Other';
    const acc = ppAccounts.find((a) => ppAccountKey(a) === val);
    if (!acc) return <Box component="span" sx={{ color: 'text.secondary' }}>Select PP account</Box>;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PpLogo ppType={acc.ppType} /><span>{formatPpAccountLabel(acc)}</span>
      </Box>
    );
  };

  const renderCrmValue = (val: string) => {
    if (val === CRM_OTHER) return 'Other';
    const acc = crmAccounts.find((a) => ppAccountKey(a) === val);
    if (!acc) return <Box component="span" sx={{ color: 'text.secondary' }}>Select CRM</Box>;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PpLogo ppType={acc.ppType} /><span>{formatPpAccountLabel(acc)}</span>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={(_, r) => { if (r !== 'backdropClick') onClose(); }} fullWidth maxWidth="md">
      <DialogTitle>{editing ? 'Edit Enrolment' : 'New Enrolment'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {!hideCompanySelect && (
            <Grid size={12}>
              <CompanySelect companies={miniCompanies} value={companyId}
                onChange={(id) => { setCompanyId(id); setPpSelection(''); setPpOtherName(''); setCrmSelection(''); setCrmText(''); setSelectedBrandId(''); }}
                disabled={companyDisabled} />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="pp-account-label" shrink>Payment Processor Account</InputLabel>
              {/* A merchant account code belongs to one Adyen account, so it cannot survive a swap. */}
              <Select labelId="pp-account-label" value={ppSelection} displayEmpty renderValue={renderPpValue}
                input={<OutlinedInput notched label="Payment Processor Account" />}
                onChange={(e) => { setPpSelection(e.target.value); setMerchantCode(''); }}>
                {processorAccounts.length === 0 && (
                  <MenuItem disabled>
                    {hideCompanySelect
                      ? 'No payment processor accounts yet'
                      : (companyId === '' ? 'Select a company first' : 'No PP accounts for this company')}
                  </MenuItem>
                )}
                {processorAccounts.map((a) => (
                  <MenuItem key={ppAccountKey(a)} value={ppAccountKey(a)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PpLogo ppType={a.ppType} /><span>{formatPpAccountLabel(a)}</span>
                    </Box>
                  </MenuItem>
                ))}
                {!hideCompanySelect && <Divider />}
                {!hideCompanySelect && <MenuItem value={PP_OTHER}>Other - enter a new PP account</MenuItem>}
              </Select>
            </FormControl>
            {!hideCompanySelect && ppSelection === PP_OTHER && (
              <TextField size="small" fullWidth sx={{ mt: 1 }} placeholder="New payment processor and account name"
                value={ppOtherName} onChange={(e) => setPpOtherName(e.target.value)} />
            )}
            {showMerchants && (
              <>
                <FormControl fullWidth size="small" sx={{ mt: 1 }} disabled={merchantsLoading || merchantError != null}>
                  <InputLabel id="adyen-merchant-label" shrink>Adyen merchant</InputLabel>
                  <Select labelId="adyen-merchant-label" value={merchantCode} displayEmpty
                    input={<OutlinedInput notched label="Adyen merchant" />}
                    renderValue={(v) => (v === '' ? <Box component="span" sx={{ color: 'text.secondary' }}>No merchant</Box> : String(v))}
                    onChange={(e) => setMerchantCode(e.target.value)}>
                    {/* The merchant is optional: an enrolment gets recorded before anyone has established it. */}
                    <MenuItem value=""><Box component="span" sx={{ color: 'text.secondary' }}>No merchant</Box></MenuItem>
                    {isUnlisted && <MenuItem value={merchantCode}>{merchantCode}</MenuItem>}
                    {(merchants ?? []).map((m) => (
                      <MenuItem key={m.id} value={m.id} disabled={isClaimedByOther(m.mappedEnrolmentId)}>
                        <Box>
                          <Typography variant="body2">{m.name ?? m.id}</Typography>
                          {m.name && <Typography variant="caption" color="text.secondary">{m.id}</Typography>}
                        </Box>
                      </MenuItem>
                    ))}
                    {merchants?.length === 0 && <MenuItem disabled>No merchant accounts returned by Adyen</MenuItem>}
                  </Select>
                </FormControl>
                {merchantError && <Alert severity="error" sx={{ mt: 1 }}>{merchantError}</Alert>}
                {isUnlisted && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {merchantCode} is no longer in the Adyen merchant list for this account. Saving keeps it; pick another to replace it.
                  </Alert>
                )}
              </>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="crm-label" shrink>CRM Account</InputLabel>
              {/* A biller id belongs to one CoC account, so it cannot survive a swap. */}
              <Select labelId="crm-label" value={crmSelection} displayEmpty renderValue={renderCrmValue}
                input={<OutlinedInput notched label="CRM Account" />}
                onChange={(e) => { setCrmSelection(e.target.value); setBillerIds([]); setGatewayIds([]); }}>
                {/* The CRM is optional, so a picked account has to be clearable again. */}
                <MenuItem value="">None</MenuItem>
                {crmAccounts.length === 0 && (
                  <MenuItem disabled>{!hideCompanySelect && companyId === '' ? 'Select a company first' : 'No CRM accounts for this company'}</MenuItem>
                )}
                {crmAccounts.map((a) => (
                  <MenuItem key={ppAccountKey(a)} value={ppAccountKey(a)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PpLogo ppType={a.ppType} /><span>{formatPpAccountLabel(a)}</span>
                    </Box>
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem value={CRM_OTHER}>Other</MenuItem>
              </Select>
            </FormControl>
            {crmSelection === CRM_OTHER && (
              <TextField size="small" fullWidth sx={{ mt: 1 }} placeholder="New CRM name" value={crmText}
                slotProps={{ htmlInput: { maxLength: 60 } }} onChange={(e) => setCrmText(e.target.value)} />
            )}
            {showGateways && (
              <GatewayMultiSelect
                label="CheckoutChamp gateways"
                value={billerIds}
                options={(gateways ?? []).map((g) => ({
                  value: g.billerId,
                  label: g.title ?? String(g.billerId),
                  // Two gateways of one processor often share a title; the MID is what tells them apart.
                  caption: [g.midNumber, g.descriptor].filter(Boolean).join(' · ') || String(g.billerId),
                  claimed: isClaimedByOther(g.mappedEnrolmentId),
                }))}
                delisted={selectedDelistedBillerIds.map((id) => ({ value: id, label: gatewayLabel(id) }))}
                loading={gatewaysLoading}
                error={gatewayError}
                emptyText="No gateways returned by CheckoutChamp"
                onChange={(values) => setBillerIds(values.map(Number))}
              />
            )}
            {showCrmGateways && (
              <GatewayMultiSelect
                label="CRM connector gateways"
                value={gatewayIds}
                options={(crmGateways ?? []).map((g) => ({
                  value: g.id,
                  label: g.name,
                  caption: g.id,
                  claimed: isClaimedByOther(g.mappedEnrolmentId),
                }))}
                delisted={selectedDelistedGatewayIds.map((id) => ({ value: id, label: crmGatewayTitles.get(id) ?? id }))}
                loading={crmGatewaysLoading}
                error={crmGatewayError}
                emptyText="No gateways returned by the CRM"
                onChange={(values) => setGatewayIds(values.map(String))}
              />
            )}
          </Grid>
          {!hideCompanySelect && (
            <Grid size={{ xs: 12, sm: 6 }}>
              {derivedBrandAccount ? (
                <>
                  <TextField
                    label={BRAND_LABEL.singular}
                    value={derivedBrandAccount.brandName ?? ''}
                    disabled
                    fullWidth
                    size="small"
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    Set on the {formatPpAccountLabel(derivedBrandAccount)} account
                  </Typography>
                </>
              ) : (
                <TextField
                  select
                  label={BRAND_LABEL.singular}
                  value={selectedBrandId}
                  onChange={(e) => setSelectedBrandId(e.target.value === '' ? '' : Number(e.target.value))}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">None</MenuItem>
                  {companyBrands.map((b) => (
                    <MenuItem key={b.brandId} value={b.brandId}>{b.brandName}</MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
          )}
          <Grid size={12}>
            <TextField size="small" fullWidth label="Descriptors (comma-separated)" value={descriptors}
              onChange={(e) => setDescriptors(e.target.value)} />
          </Grid>
          <Grid size={12}>
            <TextField size="small" fullWidth label="Historic Descriptors (comma-separated)" value={historic}
              onChange={(e) => setHistoric(e.target.value)} />
          </Grid>
          <Grid size={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>BIN / CAID / MCC combinations</Typography>
            {combos.map((c, i) => (
              <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr auto auto', gap: 1, alignItems: 'start', mb: 1.5 }}>
                <TextField size="small" fullWidth label="BIN" helperText="6–8 digits"
                  inputRef={(el: HTMLInputElement | null) => { binRefs.current[i] = el; }}
                  value={c.bin} onChange={(e) => setCombos((p) => p.map((x, j) => (j === i ? { ...x, bin: e.target.value } : x)))} />
                <TextField size="small" fullWidth label="CAID" helperText="Alphanumeric, up to 20"
                  value={c.caid} onChange={(e) => setCombos((p) => p.map((x, j) => (j === i ? { ...x, caid: e.target.value } : x)))} />
                <TextField size="small" fullWidth label="MCC" helperText="3–4 digits"
                  value={c.mcc} onChange={(e) => setCombos((p) => p.map((x, j) => (j === i ? { ...x, mcc: e.target.value } : x)))} />
                <Tooltip title="New row with the same CAID and MCC">
                  <IconButton onClick={() => copyComboRow(i)}><ContentCopyIcon /></IconButton>
                </Tooltip>
                <IconButton onClick={() => setCombos((p) => p.filter((_, j) => j !== i))} disabled={combos.length === 1}><DeleteOutlineIcon /></IconButton>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={() => setCombos((p) => [...p, emptyCombo()])}>Add combination</Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}>
          {loading ? 'Saving...' : (editing ? 'Save changes' : 'Create enrolment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
